import { useEffect, useRef, useCallback, useState } from 'react';
import { sendViolation } from '../logic/testLogic';

const SHORTCUT_KEYS = ['c', 'v', 'x', 'a', 'u', 's', 'p', 'f', 'i', 'j', 'k', 'r'];

function isFullscreenActive() {
  return !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement
  );
}

export function useBrowserLock({ sessionId, onViolation, onForceSubmit, onFullscreenExit, enabled }) {
  const violationCountRef = useRef(0);
  const enabledRef        = useRef(enabled);
  const programmaticFsExitRef = useRef(false);
  const [isFullscreen, setIsFullscreen] = useState(isFullscreenActive());
  const [isBlocked, setIsBlocked]       = useState(false);

  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  const reportViolation = useCallback(async (type, details) => {
    if (!enabledRef.current || !sessionId) return;
    try {
      violationCountRef.current += 1;
      const payload = await sendViolation(sessionId, type, details);
      if (onViolation) onViolation(payload.violation_count, payload.max_violations, type);
      if (payload.forceSubmit && onForceSubmit) onForceSubmit();
    } catch {
      // silently ignore network errors for violations
    }
  }, [sessionId, onViolation, onForceSubmit]);

  // ── Fullscreen enforcement ─────────────────────────────────
  const requestFullscreen = useCallback(() => {
    const el = document.documentElement;
    if (!isFullscreenActive()) {
      const p = el.requestFullscreen?.() || el.webkitRequestFullscreen?.() || el.mozRequestFullScreen?.();
      if (p && typeof p.then === 'function') {
        p.then(() => setIsFullscreen(true)).catch(() => {});
      }
    }
  }, []);

  const exitFullscreen = useCallback(() => {
    if (!isFullscreenActive()) {
      setIsFullscreen(false);
      return;
    }
    programmaticFsExitRef.current = true;
    const p =
      document.exitFullscreen?.() ||
      document.webkitExitFullscreen?.() ||
      document.mozCancelFullScreen?.();
    if (p && typeof p.then === 'function') {
      p
        .then(() => setIsFullscreen(false))
        .catch(() => {
          programmaticFsExitRef.current = false;
        });
    } else {
      setIsFullscreen(false);
    }
  }, []);

  const unblock = useCallback(() => {
    requestFullscreen();
    setIsBlocked(false);
  }, [requestFullscreen]);

  useEffect(() => {
    if (!enabled) return;

    const handleFsChange = () => {
      const inFs = isFullscreenActive();
      setIsFullscreen(inFs);
      if (!inFs && enabledRef.current) {
        if (programmaticFsExitRef.current) {
          programmaticFsExitRef.current = false;
          return;
        }
        reportViolation('fullscreen_exit', 'User exited fullscreen mode');
        if (onFullscreenExit) onFullscreenExit();
        setIsBlocked(true);
      }
    };

    document.addEventListener('fullscreenchange',       handleFsChange);
    document.addEventListener('webkitfullscreenchange', handleFsChange);
    document.addEventListener('mozfullscreenchange',    handleFsChange);

    return () => {
      document.removeEventListener('fullscreenchange',       handleFsChange);
      document.removeEventListener('webkitfullscreenchange', handleFsChange);
      document.removeEventListener('mozfullscreenchange',    handleFsChange);
    };
  }, [enabled, reportViolation, onFullscreenExit]);

  // ── Tab switch / window blur / minimize ────────────────────
  useEffect(() => {
    if (!enabled) return;

    const handleBlur = () => {
      if (enabledRef.current) {
        reportViolation('window_blur', 'User switched windows or minimized');
        setIsBlocked(true);
      }
    };

    const handleFocus = () => {
      // Keep blocked — candidate must click the "Return to Test" button
      // which calls unblock() and re-enters fullscreen
    };

    const handleVisibility = () => {
      if (!enabledRef.current) return;
      if (document.hidden) {
        reportViolation('tab_switch', 'User switched to another tab');
        setIsBlocked(true);
      }
    };

    window.addEventListener('blur',               handleBlur);
    window.addEventListener('focus',              handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('blur',               handleBlur);
      window.removeEventListener('focus',              handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [enabled, reportViolation]);

  // ── Right-click block ──────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    const handler = e => {
      e.preventDefault();
      reportViolation('right_click', 'Right-click attempt');
    };
    document.addEventListener('contextmenu', handler);
    return () => document.removeEventListener('contextmenu', handler);
  }, [enabled, reportViolation]);

  // ── Copy/paste + keyboard shortcuts ───────────────────────
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = e => {
      if (!enabledRef.current) return;

      // Block Ctrl/Cmd + specific keys (including R for refresh)
      if ((e.ctrlKey || e.metaKey) && SHORTCUT_KEYS.includes(e.key.toLowerCase())) {
        e.preventDefault();
        if (['c', 'v', 'x'].includes(e.key.toLowerCase())) {
          reportViolation('copy_paste', `Ctrl+${e.key.toUpperCase()} blocked`);
        } else if (e.key.toLowerCase() === 'r') {
          reportViolation('page_refresh', 'Ctrl+R refresh blocked');
        } else {
          reportViolation('keyboard_shortcut', `Blocked: Ctrl+${e.key.toUpperCase()}`);
        }
      }

      // Block Ctrl+Tab (tab switching)
      if ((e.ctrlKey || e.metaKey) && e.key === 'Tab') {
        e.preventDefault();
        reportViolation('tab_switch', 'Ctrl+Tab tab switch attempted');
      }

      // Block F-keys (F5 refresh, F12 devtools, etc.)
      if (e.key.startsWith('F') && !isNaN(parseInt(e.key.slice(1)))) {
        e.preventDefault();
        if (e.key === 'F12') reportViolation('devtools_open', 'F12 key pressed');
        else if (e.key === 'F5') reportViolation('page_refresh', 'F5 refresh attempted');
        else reportViolation('keyboard_shortcut', `${e.key} blocked`);
      }

      // Block Alt+Tab (partial — browser limitation)
      if (e.altKey && e.key === 'Tab') {
        e.preventDefault();
        reportViolation('tab_switch', 'Alt+Tab attempted');
      }

      // Block Alt+F4 / Alt+Left/Right (window close / browser nav)
      if (e.altKey && (e.key === 'F4' || e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        reportViolation('page_refresh', `Alt+${e.key} navigation blocked`);
      }

      // Block Ctrl+Shift+I / J / C (devtools)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && ['i', 'j', 'c'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        reportViolation('devtools_open', 'DevTools shortcut blocked');
      }
    };

    const handleCopy  = e => { e.preventDefault(); reportViolation('copy_paste', 'Copy attempt'); };
    const handlePaste = e => { e.preventDefault(); reportViolation('copy_paste', 'Paste attempt'); };
    const handleCut   = e => { e.preventDefault(); reportViolation('copy_paste', 'Cut attempt'); };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('copy',    handleCopy);
    document.addEventListener('paste',   handlePaste);
    document.addEventListener('cut',     handleCut);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('copy',    handleCopy);
      document.removeEventListener('paste',   handlePaste);
      document.removeEventListener('cut',     handleCut);
    };
  }, [enabled, reportViolation]);

  // ── DevTools detection (window size heuristic) ────────────
  useEffect(() => {
    if (!enabled) return;
    let reported = false;

    const checkDevtools = () => {
      const threshold  = 160;
      const widthDiff  = window.outerWidth  - window.innerWidth;
      const heightDiff = window.outerHeight - window.innerHeight;
      if ((widthDiff > threshold || heightDiff > threshold) && !reported) {
        reported = true;
        reportViolation('devtools_open', 'DevTools likely opened (window size change)');
      }
      if (widthDiff <= threshold && heightDiff <= threshold) reported = false;
    };

    const interval = setInterval(checkDevtools, 2000);
    window.addEventListener('resize', checkDevtools);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', checkDevtools);
    };
  }, [enabled, reportViolation]);

  // ── History navigation lock (back / forward) ──────────────
  useEffect(() => {
    if (!enabled) return;
    // Push a dummy state so the back button doesn't navigate away
    window.history.pushState(null, '', window.location.href);
    const handler = () => {
      window.history.pushState(null, '', window.location.href);
      if (enabledRef.current) reportViolation('page_refresh', 'Back/forward navigation blocked');
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [enabled, reportViolation]);

  // ── Multiple-tab detection via BroadcastChannel ────────────
  useEffect(() => {
    if (!enabled || !sessionId) return;
    if (!window.BroadcastChannel) return;

    const channel = new BroadcastChannel(`aptitude_test_${sessionId}`);

    // Announce our presence; if another tab pings back, report it
    channel.postMessage('ping');

    channel.onmessage = (e) => {
      if (e.data === 'ping') {
        // Reply to inform the other tab they are a duplicate
        channel.postMessage('duplicate');
      }
      if (e.data === 'duplicate') {
        reportViolation('tab_switch', 'Test opened in multiple tabs or windows');
      }
    };

    return () => channel.close();
  }, [enabled, sessionId, reportViolation]);

  // ── beforeunload (refresh / close / navigation) ───────────
  useEffect(() => {
    if (!enabled) return;
    const handler = e => {
      e.preventDefault();
      e.returnValue = '';
      if (sessionId) {
        navigator.sendBeacon?.(
          '/api/test/violation',
          JSON.stringify({ sessionId, violationType: 'page_refresh', details: 'Page unload attempted' })
        );
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [enabled, sessionId]);

  return { requestFullscreen, exitFullscreen, isFullscreen, isBlocked, unblock };
}
